import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faDollarSign, 
  faCalendar, 
  faFileAlt, 
  faUser, 
  faPen
} from '@fortawesome/free-solid-svg-icons';
import { fetchReceitaById } from '../../../services/receitaService';

const ReceitaView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [receita, setReceita] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadReceita = async () => {
      try {
        setLoading(true);
        const data = await fetchReceitaById(parseInt(id));
        console.log('Dados da receita:', data);
        if (data) {
          setReceita(data);
        } else {
          setError('Receita não encontrada');
        }
      } catch (err) {
        setError('Erro ao carregar receita: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadReceita();
    }
  }, [id]);

  const handleBack = () => {
    navigate('/financeiro/receitas');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';

    try {
      // Se a data vem no formato ISO com parte de tempo, preferir somente a parte YYYY-MM-DD
      if (typeof dateString === 'string' && dateString.includes('T')) {
        const datePart = dateString.split('T')[0];
        const [year, month, day] = datePart.split('-').map(Number);
        // Criar Date usando componentes (ano, mês-1, dia) para evitar shift por UTC
        const d = new Date(year, month - 1, day);
        return d.toLocaleDateString('pt-BR');
      }

      // Caso já esteja em YYYY-MM-DD
      if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split('-').map(Number);
        const d = new Date(year, month - 1, day);
        return d.toLocaleDateString('pt-BR');
      }

      // Fallback para outros formatos
      return new Date(dateString).toLocaleDateString('pt-BR');
    } catch (err) {
      console.warn('Erro ao formatar data:', dateString, err);
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="flex-1 w-full max-w-full overflow-x-hidden">
        <div className="px-2 sm:px-6">
          <div className="text-center">
            <p className="text-gray-500">Carregando...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 w-full max-w-full overflow-x-hidden">
        <div className="px-2 sm:px-6">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
          <div className="mt-4">
            <button
              onClick={handleBack}
              className="text-blue-600 hover:text-blue-800 transition-colors"
            >
              ← Voltar para lista
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!receita) {
    return (
      <div className="flex-1 w-full max-w-full overflow-x-hidden">
        <div className="px-2 sm:px-6">
          <div className="text-center">
            <p className="text-gray-500">Receita não encontrada.</p>
            <button
              onClick={handleBack}
              className="mt-4 text-blue-600 hover:text-blue-800 transition-colors"
            >
              ← Voltar para lista
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 w-full max-w-full overflow-x-hidden">
      <div className="px-2 sm:px-6">
        <div className="max-w-full">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <button
                  onClick={handleBack}
                  className="mr-4 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  <FontAwesomeIcon icon={faArrowLeft} size="lg" />
                </button>
                <div>
                  <h1 className="text-3xl font-bold text-gray-800">
                    Receita #{receita.idEntrada}
                  </h1>
                  <p className="text-gray-600 mt-2">{receita.descricao}</p>
                </div>
              </div>
              <div className="flex space-x-3">
                <Link
                  to={`/financeiro/receitas/${receita.idEntrada}/edit`}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg inline-flex items-center transition-colors"
                >
                  <FontAwesomeIcon icon={faPen} className="mr-2" />
                  Editar
                </Link>
              </div>
            </div>
          </div>

          {/* Informações Principais */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-6">Informações da Receita</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-green-100 text-green-600 mr-4">
                      <FontAwesomeIcon icon={faDollarSign} />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Valor</p>
                      <p className="text-2xl font-bold text-green-600">
                        {formatCurrency(receita.valor)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-blue-100 text-blue-600 mr-4">
                      <FontAwesomeIcon icon={faCalendar} />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Data de Balanço</p>
                      <p className="text-lg font-semibold text-gray-800">
                        {formatDate(receita.dataBalanco)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-purple-100 text-purple-600 mr-4">
                      <FontAwesomeIcon icon={faUser} />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Conta</p>
                      <p className="text-lg font-semibold text-gray-800">
                        {receita.contaDescricao || receita.conta}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-orange-100 text-orange-600 mr-4">
                      <FontAwesomeIcon icon={faFileAlt} />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Pedido</p>
                      <p className="text-lg font-semibold text-gray-800">
                        {receita.pedido || 'N/A'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-indigo-100 text-indigo-600 mr-4">
                      <FontAwesomeIcon icon={faFileAlt} />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Quantidade</p>
                      <p className="text-lg font-semibold text-gray-800">
                        {receita.quantidade}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Status Card */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-6">Informações do Sistema</h2>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Cadastrado Por</span>
                    <span className="text-sm font-medium text-gray-800">
                      {receita.cadastradoPor}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Data Cadastro</span>
                    <span className="text-sm text-gray-800">
                      {formatDate(receita.dataInclusao)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Ações */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-6">Ações</h2>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => window.print()}
                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md transition-colors"
              >
                Imprimir
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceitaView;
